/**
 * Core Financial Calculations
 *
 * Deterministic mathematical functions for job pricing, commission, GST,
 * variation splits, and proportional payment milestone allocation.
 * Zero external dependencies — compatible with Deno Edge Functions and Node/Vite runtimes.
 */

export const INITIAL_PAYOUT_PERCENTAGE = 60;
export const DEFERRED_PAYOUT_PERCENTAGE = 40;
export const INITIAL_PAYOUT_RATIO = 0.6;
export const DEFERRED_PAYOUT_RATIO = 0.4;
export const LEGACY_INITIAL_PAYOUT_PERCENTAGE = 80;
export const LEGACY_DEFERRED_PAYOUT_PERCENTAGE = 20;
export const LEGACY_INITIAL_PAYOUT_RATIO = 0.8;
export const LEGACY_DEFERRED_PAYOUT_RATIO = 0.2;
export const PAYOUT_SPLIT_60_40_CUTOVER_DATE = "2026-08-03T00:00:00.000Z";

export interface TradeCompanyGstInput {
  gst_registered?: boolean | null;
  gst_effective_from?: string | Date | null;
}

/**
 * Trade GST calculation for edge and shared code.
 * TS is effective-date-aware and SQL is registration-only; they agree while no trade has a future gst_effective_from:
 * registered AND (effective_from null OR <= asOf) -> round(net * 0.10) else 0.
 */
/**
 * Is the trade GST-registered as of `asOf`?
 * TS is effective-date-aware and SQL trade_gst_payout_cents is registration-only; they agree while no trade has a future gst_effective_from:
 * gst_registered AND (gst_effective_from IS NULL OR gst_effective_from <= as_of).
 * An unparseable effective date is treated as "no date" (registered), matching the SQL NULL branch.
 */
export function isTradeGstRegistered(
  tradeCompany: TradeCompanyGstInput | null | undefined,
  asOf: Date | string = new Date()
): boolean {
  if (!tradeCompany || !tradeCompany.gst_registered) return false;
  if (tradeCompany.gst_effective_from) {
    const effectiveDate = new Date(tradeCompany.gst_effective_from);
    const asOfDate = asOf instanceof Date ? asOf : new Date(asOf);
    if (!isNaN(effectiveDate.getTime()) && !isNaN(asOfDate.getTime()) && effectiveDate.getTime() > asOfDate.getTime()) {
      return false;
    }
  }
  return true;
}

export function tradeGstCents(
  netCents: number,
  tradeCompany: TradeCompanyGstInput | null | undefined,
  asOf: Date | string = new Date()
): number {
  if (netCents <= 0 || !isTradeGstRegistered(tradeCompany, asOf)) {
    return 0;
  }
  return Math.round(netCents * 0.10);
}

/**
 * Standard 10% GST on ex-GST cents, rounded to integer cents.
 */
export function calculateGst(exGstCents: number): number {
  if (exGstCents <= 0) return 0;
  return Math.round(exGstCents * 0.1);
}

export interface PayoutSplitResult {
  initialCents: number;
  holdbackCents: number;
  initialGstCents: number;
  holdbackGstCents: number;
  initialNetCents: number;
  holdbackNetCents: number;
}

/**
 * Payout split formula (TS mirror of public.fin_payout_split).
 *
 * initial = round((net + gst) * pct / 100)
 * holdback = (net + gst) - initial
 * initialGst = round(gst * pct / 100)
 * holdbackGst = gst - initialGst
 */
export function computePayoutSplit(
  tradeNetCents: number,
  tradeGstCents: number,
  initialPercentage: number = INITIAL_PAYOUT_PERCENTAGE,
): PayoutSplitResult {
  const net = Math.round(tradeNetCents || 0);
  const gst = Math.round(tradeGstCents || 0);
  const pct = Math.max(0, Math.min(100, Math.round(initialPercentage)));

  const share = net + gst;
  const initialCents = Math.round((share * pct) / 100);
  const holdbackCents = share - initialCents;
  const initialGstCents = Math.round((gst * pct) / 100);
  const holdbackGstCents = gst - initialGstCents;
  const initialNetCents = initialCents - initialGstCents;
  const holdbackNetCents = holdbackCents - holdbackGstCents;

  return {
    initialCents,
    holdbackCents,
    initialGstCents,
    holdbackGstCents,
    initialNetCents,
    holdbackNetCents,
  };
}

export interface JobFinancials {
  clientTotalCents: number;
  commissionBps: number;
  tradeGstRegistered: boolean;
}

export interface JobFinancialsResult {
  exGstCents: number;
  commissionCents: number;
  tradeNetCents: number;
  gstAmountCents: number;
  tradeGstCents: number;
}

export interface VariationFinancialsResult {
  tradeGstCents: number;
  totalTransferCents: number;
}

/**
 * Computes trade GST and total transfer amount for a job variation payout/transfer.
 */
export function computeVariationFinancials(
  tradeAmountCents: number,
  tradeGstRegistered: boolean
): VariationFinancialsResult {
  const tradeGstCents = tradeGstRegistered
    ? Math.round(tradeAmountCents * 0.10)
    : 0;
  const totalTransferCents = tradeAmountCents + tradeGstCents;
  return { tradeGstCents, totalTransferCents };
}

export interface VariationAmountInput {
  /** Trade-entered variation amount, in dollars. */
  amount: number;
  /** Commission rate in basis points (e.g. 2000 = 20%). */
  commissionRateBps: number;
  /** Client markup percentage applied on top of trade+commission (e.g. 10 = 10%). */
  markupPercentage: number;
}

export interface VariationAmounts {
  tradeAmountCents: number;
  commissionAmountCents: number;
  markupAmountCents: number;
  subtotalCents: number;
  gstAmountCents: number;
  customerTotalCents: number;
}

/**
 * Computes the full financial breakdown for a variation.
 *
 * Formula:
 * 1. Trade amount = amount * 100 (in cents)
 * 2. Commission = round(Trade * commissionRateBps / 10000)
 * 3. Markup = round((Trade + Commission) * markupPercentage / 100)
 * 4. Subtotal = Trade + Commission + Markup
 * 5. GST = round(Subtotal * 0.10)
 * 6. Customer Total = Subtotal + GST
 */
export function computeVariationAmounts(
  { amount, commissionRateBps, markupPercentage }: VariationAmountInput,
): VariationAmounts {
  const tradeAmountCents = Math.round(amount * 100);
  const commissionAmountCents = Math.round(
    (tradeAmountCents * commissionRateBps) / 10000,
  );
  const markupAmountCents = Math.round(
    ((tradeAmountCents + commissionAmountCents) * markupPercentage) / 100,
  );
  const subtotalCents = tradeAmountCents + commissionAmountCents + markupAmountCents;
  const gstAmountCents = Math.round(subtotalCents * 0.10);
  const customerTotalCents = subtotalCents + gstAmountCents;

  return {
    tradeAmountCents,
    commissionAmountCents,
    markupAmountCents,
    subtotalCents,
    gstAmountCents,
    customerTotalCents,
  };
}

export interface MilestoneInput {
  label: string;
  amountCents: number;
}

export interface MilestoneSplit extends MilestoneInput {
  exGstCents: number;
  gstAmountCents: number;
  commissionCents: number;
  tradeNetCents: number;
  tradeGstCents: number;
}

/**
 * Standard Australian GST inclusive math.
 * Ex-GST = clientTotal / 1.1
 * Commission = Ex-GST * commissionBps / 10000
 * Trade Net = Ex-GST - Commission
 */
export function computeJobFinancials(f: JobFinancials): JobFinancialsResult {
  const exGstCents = Math.round(f.clientTotalCents / 1.1);
  const commissionCents = Math.round((exGstCents * f.commissionBps) / 10000);
  const tradeNetCents = exGstCents - commissionCents;
  const gstAmountCents = f.clientTotalCents - exGstCents;
  const tradeGstCents = f.tradeGstRegistered ? Math.round(tradeNetCents * 0.10) : 0;

  return {
    exGstCents,
    commissionCents,
    tradeNetCents,
    gstAmountCents,
    tradeGstCents,
  };
}

/**
 * Proportional commission and GST allocation across payment milestones.
 * The final milestone absorbs rounding remainders to guarantee exact reconciliation.
 */
export function allocateMilestoneSplits(
  f: JobFinancials,
  milestones: MilestoneInput[]
): MilestoneSplit[] {
  if (milestones.length === 0) {
    throw new Error("At least one milestone is required");
  }
  for (const m of milestones) {
    if (!Number.isInteger(m.amountCents) || m.amountCents <= 0) {
      throw new Error(`Milestone "${m.label}" must have a positive integer amount_cents`);
    }
  }
  const sumAmounts = milestones.reduce((s, m) => s + m.amountCents, 0);
  if (sumAmounts !== f.clientTotalCents) {
    throw new Error(
      `Milestone amounts (${sumAmounts}) must sum to the job total (${f.clientTotalCents})`
    );
  }

  const job = computeJobFinancials(f);

  let allocExGst = 0;
  let allocCommission = 0;
  let allocTradeNet = 0;
  let allocGst = 0;
  let allocTradeGst = 0;

  return milestones.map((m, idx) => {
    const isLast = idx === milestones.length - 1;
    let exGstCents: number;
    let commissionCents: number;
    let tradeNetCents: number;
    let gstAmountCents: number;
    let tradeGstCents: number;

    if (isLast) {
      exGstCents = job.exGstCents - allocExGst;
      commissionCents = job.commissionCents - allocCommission;
      tradeNetCents = job.tradeNetCents - allocTradeNet;
      gstAmountCents = job.gstAmountCents - allocGst;
      tradeGstCents = job.tradeGstCents - allocTradeGst;
    } else {
      const share = m.amountCents / f.clientTotalCents;
      exGstCents = Math.round(job.exGstCents * share);
      commissionCents = Math.round(job.commissionCents * share);
      tradeNetCents = exGstCents - commissionCents;
      gstAmountCents = m.amountCents - exGstCents;
      tradeGstCents = f.tradeGstRegistered ? Math.round(tradeNetCents * 0.10) : 0;

      allocExGst += exGstCents;
      allocCommission += commissionCents;
      allocTradeNet += tradeNetCents;
      allocGst += gstAmountCents;
      allocTradeGst += tradeGstCents;
    }

    return {
      label: m.label,
      amountCents: m.amountCents,
      exGstCents,
      gstAmountCents,
      commissionCents,
      tradeNetCents,
      tradeGstCents,
    };
  });
}

/**
 * jobs.payout_status 'HOLDBACK_REFUNDED': the trade KEPT its initial payout and the holdback went
 * back to the client (a full held refund, before or after the final release). Distinct from
 * 'REFUNDED', which means everything went back to the client.
 */
export const HOLDBACK_REFUNDED_PAYOUT_STATUS = 'HOLDBACK_REFUNDED';
export const DISPUTE_SETTLED_PAYOUT_STATUS = 'DISPUTE_SETTLED';

/** Payout statuses after which nothing more will be released to the trade. */
export const SETTLED_PAYOUT_STATUSES = [
  'FULLY_RELEASED',
  'REFUNDED',
  HOLDBACK_REFUNDED_PAYOUT_STATUS,
  DISPUTE_SETTLED_PAYOUT_STATUS,
  'CANCELLED',
] as const;

/** True when nothing more will be released to the trade for this job. */
export function isPayoutSettled(payoutStatus: string | null | undefined): boolean {
  return (SETTLED_PAYOUT_STATUSES as readonly string[]).includes(payoutStatus ?? '');
}

/** Human label for a jobs.payout_status, for every chip / line that shows it. */
export function payoutStatusLabel(payoutStatus: string | null | undefined): string {
  switch (payoutStatus) {
    case null:
    case undefined:
    case '':
    case 'PENDING':
      return 'Payout pending';
    case 'PAYOUT_PENDING':
    case 'PAYING':
      return 'Payout processing';
    case 'PARTIALLY_RELEASED':
      return 'Initial payment released';
    case 'FULLY_RELEASED':
      return 'Fully released';
    case 'REFUNDED':
      return 'Refunded';
    case HOLDBACK_REFUNDED_PAYOUT_STATUS:
      return 'Holdback refunded';
    case DISPUTE_SETTLED_PAYOUT_STATUS:
      return 'Dispute settled';
    case 'DISPUTED':
      return 'Disputed';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return payoutStatus
        .toLowerCase()
        .split('_')
        .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ');
  }
}

/** PostgREST `in` list of SETTLED_PAYOUT_STATUSES, for `.not('payout_status', 'in', ...)`. */
export const SETTLED_PAYOUT_STATUSES_FILTER = `(${SETTLED_PAYOUT_STATUSES.join(',')})`;

export interface JobPayoutSplitInput {
  tradeNetCents: number;
  tradeGstCents?: number;
  completedAt?: string | null;
  payoutStatus?: string | null;
  /** Explicit override if recorded in transaction metadata or DB */
  initialPayoutPercentage?: number | null;
  settledTradeAmountCents?: number | null;
}

export interface JobPayoutSplitResult {
  initialPercentage: number;
  deferredPercentage: number;
  initialNetCents: number;
  initialGstCents: number;
  initialTotalCents: number;
  holdbackNetCents: number;
  holdbackGstCents: number;
  holdbackTotalCents: number;
  completedAmountCents: number;
  completedGstCents: number;
  pendingAmountCents: number;
  pendingGstCents: number;
}

/**
 * Calculates initial vs. deferred (holdback) payout amounts for a job,
 * taking into account historical cutover (80/20 prior to Aug 3, 2026 vs 60/40 after)
 * and current payout status (FULLY_RELEASED, PARTIALLY_RELEASED, etc.).
 */
export function calculateJobPayoutSplit(input: JobPayoutSplitInput): JobPayoutSplitResult {
  const tn = input.tradeNetCents || 0;
  const tg = input.tradeGstCents || 0;
  const status = input.payoutStatus || 'PENDING';

  let initialPercentage = INITIAL_PAYOUT_PERCENTAGE; // Default 60%
  let deferredPercentage = DEFERRED_PAYOUT_PERCENTAGE; // Default 40%

  if (
    typeof input.initialPayoutPercentage === 'number' &&
    Number.isFinite(input.initialPayoutPercentage)
  ) {
    initialPercentage = Math.max(0, Math.min(100, Math.round(input.initialPayoutPercentage)));
    deferredPercentage = 100 - initialPercentage;
  } else if (input.completedAt) {
    const completedDate = new Date(input.completedAt).getTime();
    const cutoverDate = new Date(PAYOUT_SPLIT_60_40_CUTOVER_DATE).getTime();
    if (!isNaN(completedDate) && completedDate < cutoverDate) {
      initialPercentage = LEGACY_INITIAL_PAYOUT_PERCENTAGE;
      deferredPercentage = LEGACY_DEFERRED_PAYOUT_PERCENTAGE;
    }
  }

  const split = computePayoutSplit(tn, tg, initialPercentage);
  const holdbackNetCents = split.holdbackNetCents;
  const holdbackGstCents = split.holdbackGstCents;
  const holdbackTotalCents = split.holdbackCents;

  const initialNetCents = split.initialNetCents;
  const initialGstCents = split.initialGstCents;
  const initialTotalCents = split.initialCents;

  let completedAmountCents = 0;
  let completedGstCents = 0;
  let pendingAmountCents = 0;
  let pendingGstCents = 0;

  if (status === 'FULLY_RELEASED') {
    completedAmountCents = tn;
    completedGstCents = tg;
    pendingAmountCents = 0;
    pendingGstCents = 0;
  } else if (status === 'PARTIALLY_RELEASED') {
    completedAmountCents = initialNetCents;
    completedGstCents = initialGstCents;
    pendingAmountCents = holdbackNetCents;
    pendingGstCents = holdbackGstCents;
  } else if (status === 'HOLDBACK_REFUNDED') {
    // The trade kept its initial payout; the holdback went back to the client. Its earnings are
    // the initial portion only, and nothing more is owed.
    completedAmountCents = initialNetCents;
    completedGstCents = initialGstCents;
    pendingAmountCents = 0;
    pendingGstCents = 0;
  } else if (status === 'DISPUTE_SETTLED') {
    if (input.settledTradeAmountCents != null) {
      const settled = input.settledTradeAmountCents;
      const totalTradeShare = tn + tg;
      if (totalTradeShare > 0) {
        completedAmountCents = Math.round((settled * tn) / totalTradeShare);
        completedGstCents = settled - completedAmountCents;
      } else {
        completedAmountCents = settled;
        completedGstCents = 0;
      }
    } else {
      // Legacy or not yet written: fall back to the initial portion, never the full share
      completedAmountCents = initialNetCents;
      completedGstCents = initialGstCents;
    }
    pendingAmountCents = 0;
    pendingGstCents = 0;
  } else if (status !== 'REFUNDED' && status !== 'CANCELLED') {
    // PENDING, PAYOUT_PENDING - no money released yet
    completedAmountCents = 0;
    completedGstCents = 0;
    pendingAmountCents = tn;
    pendingGstCents = tg;
  }

  return {
    initialPercentage,
    deferredPercentage,
    initialNetCents,
    initialGstCents,
    initialTotalCents,
    holdbackNetCents,
    holdbackGstCents,
    holdbackTotalCents,
    completedAmountCents,
    completedGstCents,
    pendingAmountCents,
    pendingGstCents,
  };
}

